import type { AxiosInstance } from "axios";
import { Check, Clock, Copy, DollarSign, ExternalLink, Layers, MapPin, RefreshCw, Search, ShoppingBag, TrendingUp, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "./TableResizeHandle.js";
import type { DateRange } from "../lib/dates.js";
import {
  fetchJourneyOrders,
  formatConversionTime,
  journeyMoney,
  type CustomerJourneyOrderItem,
  type CustomerJourneyOrdersResponse,
  type JourneyModel,
  type JourneyTarget,
} from "../lib/customer-journey.js";
import { Button } from "./ui/button.js";
import { InfoTooltip } from "./ui/info-tooltip.js";
import { Input } from "./ui/input.js";
import { Select } from "./ui/select.js";
import { Skeleton } from "./ui/skeleton.js";

/**
 * Customer Journey & Orders MODAL — ported from `ecom_code`'s `CustomerJourneyModal.jsx`,
 * opened from the Purchases cells of Source / Meta / Google Attribution and listing the
 * orders BEHIND the number that was clicked, with the same 22 columns, in the same order,
 * as the standalone report.
 *
 * WHAT IS NOT LEGACY'S, and why:
 *  - The rows come from this platform's own report definitions (see `customer-journey-
 *    orders.ts` in `packages/ch`), so a cell reading 41 opens 41 orders.
 *  - Email and Location are the real values, not masked — the owner's decision for this
 *    whole report (2026-09-18); legacy masked the email and offered the raw one only via
 *    a copy icon.
 *  - Search matches order number and product only. Legacy also searched email, name and
 *    city, which live in Postgres here, not in the ClickHouse query that does the search.
 *  - No column sorting: legacy's modal kept a sort state but never rendered a sort control.
 *
 * Table fonts/sizes follow this platform's other tables; the colours are legacy's own.
 */

interface Col {
  readonly key: string;
  readonly label: string;
  readonly tip: string;
  readonly align: "left" | "center" | "right";
  readonly width: number;
}

// Legacy's exact column order, labels, hint copy and widths (CustomerJourneyModal.jsx 156-637).
const COLUMNS: readonly Col[] = [
  { key: "order_name", label: "Order Name", tip: "Unique Shopify Order identifier. Click to copy without #", align: "left", width: 125 },
  { key: "email", label: "Email", tip: "Customer contact email address", align: "left", width: 200 },
  { key: "date", label: "Date", tip: "Exact order placement timestamp", align: "left", width: 165 },
  { key: "order_value", label: "Order Value", tip: "Total final order gross amount in INR", align: "right", width: 120 },
  { key: "discount", label: "Discount", tip: "Coupons and promotional discounts applied", align: "right", width: 105 },
  { key: "order_status", label: "Order Status", tip: "Order fulfillment status", align: "center", width: 130 },
  { key: "financial_status", label: "Financial Status", tip: "Shopify order payment status", align: "center", width: 130 },
  { key: "user_type", label: "User Type", tip: "Customer lifetime purchase sequence: New (1st order) vs Returning", align: "center", width: 100 },
  { key: "total_ads", label: "Total Ads", tip: "Total paid ad touchpoint sessions in this customer journey", align: "center", width: 95 },
  { key: "unique_ads", label: "Unique Ads", tip: "Distinct ad creatives or variations clicked in the journey", align: "center", width: 100 },
  { key: "conversion_time", label: "Conversion Time", tip: "Duration from customer's 1st recorded session to order placement", align: "left", width: 145 },
  { key: "session_count", label: "Session Count", tip: "Total number of distinct website sessions recorded", align: "center", width: 110 },
  { key: "touch_points", label: "Touch Points", tip: "Full multi-touch sequence of marketing channels from discovery to purchase", align: "left", width: 270 },
  { key: "first_touch", label: "First Touch", tip: "Initial acquisition channel that first introduced the customer", align: "left", width: 135 },
  { key: "first_touch_campaign", label: "First Touch Campaign", tip: "Initial ad campaign name from the customer's very first visit", align: "left", width: 190 },
  { key: "last_touch", label: "Last Touch", tip: "Immediate converting touchpoint right before purchase", align: "left", width: 135 },
  { key: "last_touch_campaign", label: "Last Touch Campaign", tip: "Immediate converting ad campaign name", align: "left", width: 190 },
  { key: "location", label: "Location", tip: "Customer delivery city and province", align: "left", width: 170 },
  { key: "product_detail", label: "Product Detail", tip: "Click to open customer's live Shopify order status page", align: "left", width: 150 },
  { key: "total_items", label: "Total Items", tip: "Count of distinct product line items (SKUs) in this order", align: "center", width: 105 },
  { key: "total_quantity", label: "Total Quantity", tip: "Total number of product units purchased across all line items in this order", align: "center", width: 110 },
  { key: "order_source", label: "Order Source", tip: "Sales channel or device platform where the order was placed (Web, Mobile App, POS, Draft Order, Shop App)", align: "left", width: 120 },
];


const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const MODEL_BADGE: Readonly<Record<JourneyModel, { readonly label: string; readonly className: string }>> = {
  fc: { label: "First-Click (FC)", className: "bg-blue-500 text-white" },
  lc: { label: "Last-Click (LC)", className: "bg-purple-600 text-white" },
  dda: { label: "Data-Driven (DDA)", className: "bg-emerald-600 text-white" },
  single_session: { label: "Single Session", className: "bg-teal-600 text-white" },
  triple: { label: "Triple Attribution", className: "bg-indigo-600 text-white" },
  linear: { label: "Linear Attribution", className: "bg-amber-600 text-white" },
};

/** Legacy's modal `formatTouchpointLabel` — normalises whatever the query passed through to one of six channel labels. */
function touchLabel(src: string): string {
  if (src === "") return "Direct";
  const c = src.trim().toLowerCase();
  if (c.includes("google-organic") || (c.includes("google") && c.includes("organic"))) return "Google Organic";
  if (c.includes("meta-organic") || (c.includes("meta") && c.includes("organic")) || (c.includes("facebook") && c.includes("organic"))) return "Meta Organic";
  if (c.includes("facebook ads") || (c.includes("facebook") && (c.includes("paid") || c.includes("ad")))) return "Facebook Ads";
  if (c.includes("google ads") || (c.includes("google") && (c.includes("paid") || c.includes("ad") || c.includes("cpc")))) return "Google Ads";
  if (c === "meta" || c === "facebook" || c === "an") return "Facebook Ads";
  if (c === "google") return "Google Ads";
  if (c === "instagram" || c === "ig") return "Instagram";
  if (c === "direct") return "Direct";
  return src.trim();
}

/** Legacy's modal `getBadgeStyle`, as Tailwind classes. */
function badgeClasses(label: string): string {
  const l = label.toLowerCase();
  if (l.includes("facebook ads") || l === "meta" || l === "facebook") return "bg-blue-50 text-blue-700 border-blue-200";
  if (l.includes("google ads")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (l.includes("meta organic") || l.includes("instagram")) return "bg-purple-50 text-purple-700 border-purple-200";
  if (l.includes("google organic")) return "bg-teal-50 text-teal-700 border-teal-200";
  if (l.includes("direct")) return "bg-slate-100 text-slate-700 border-slate-300";
  return "bg-amber-50 text-amber-700 border-amber-200";
}

/** Ant Design's preset Tag colours, which is what legacy's status columns render. */
const TAG = {
  success: { bg: "#f6ffed", text: "#52c41a", border: "#b7eb8f" },
  warning: { bg: "#fffbe6", text: "#d48806", border: "#ffe58f" },
  error: { bg: "#fff2f0", text: "#ff4d4f", border: "#ffccc7" },
  orange: { bg: "#fff7e6", text: "#fa8c16", border: "#ffd591" },
  purple: { bg: "#f9f0ff", text: "#722ed1", border: "#d3adf7" },
  neutral: { bg: "#fafafa", text: "#595959", border: "#d9d9d9" },
} as const;

/**
 * Legacy checks `delivered || fulfilled` BEFORE `unfulfilled || partial`, and both of the
 * latter contain "fulfilled" — so in legacy an Unfulfilled or Partially Fulfilled order
 * renders in the green "success" tag and the amber branch below it can never run. The
 * evident intent is amber for those two, so this checks them first.
 */
function orderStatusTag(status: string): (typeof TAG)[keyof typeof TAG] {
  const s = status.toLowerCase();
  if (s.includes("cancel")) return TAG.error;
  if (s.includes("unfulfilled") || s.includes("partial")) return TAG.warning;
  if (s.includes("delivered") || s.includes("fulfilled")) return TAG.success;
  return TAG.neutral;
}

function financialTag(status: string): (typeof TAG)[keyof typeof TAG] {
  const s = status.toLowerCase();
  if (s.includes("paid")) return TAG.success;
  if (s.includes("pending")) return TAG.orange;
  if (s.includes("refund")) return TAG.purple;
  return TAG.neutral;
}

function Tag({ colors, children }: { readonly colors: (typeof TAG)[keyof typeof TAG]; readonly children: string }): React.JSX.Element {
  return (
    <span
      className="inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap uppercase"
      style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}
    >
      {children}
    </span>
  );
}

/** Click-to-copy with a brief inline confirmation — legacy raised an Ant toast; this platform has no toast layer. */
function CopyButton({ value, label }: { readonly value: string; readonly label: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      aria-label={`Copy ${label}`}
      title={copied ? "Copied!" : `Copy ${label}`}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="text-muted-foreground/60 shrink-0 cursor-pointer transition-colors hover:text-blue-600"
    >
      {copied ? <Check className="size-3 text-emerald-600" /> : <Copy className="size-3" />}
    </button>
  );
}

/**
 * A panel that opens under its trigger on hover, portalled to the body — the modal's own
 * table scrolls (and clips), so a plain absolutely-positioned panel would be cut off. Stays
 * open while the pointer is over the panel itself, so a long journey can be scrolled.
 */
function HoverPanel({ trigger, children }: { readonly trigger: React.ReactNode; readonly children: React.ReactNode }): React.JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const open = (): void => {
    window.clearTimeout(timer.current);
    const rect = ref.current?.getBoundingClientRect();
    if (rect === undefined) return;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 340));
    setPos(
      window.innerHeight - rect.bottom < 280
        ? { bottom: window.innerHeight - rect.top + 6, left }
        : { top: rect.bottom + 6, left },
    );
  };
  const close = (): void => {
    timer.current = window.setTimeout(() => setPos(null), 120);
  };

  return (
    <span ref={ref} onMouseEnter={open} onMouseLeave={close} className="inline-flex">
      {trigger}
      {pos !== null &&
        createPortal(
          <div
            role="tooltip"
            onMouseEnter={open}
            onMouseLeave={close}
            style={{ position: "fixed", left: pos.left, ...(pos.top === undefined ? { bottom: pos.bottom } : { top: pos.top }) }}
            className="z-[70] max-h-64 w-80 max-w-[90vw] overflow-y-auto rounded-md bg-[#262626] px-2.5 py-2 text-xs text-white shadow-lg"
          >
            {children}
          </div>,
          document.body,
        )}
    </span>
  );
}

const MAX_VISIBLE_TOUCHES = 3;

/** Legacy's Touch Points cell: up to three channel badges, "+N" for the rest, and — for DDA / Linear / Triple — each touch's share of the order. */
function TouchPoints({ order, showCredit }: { readonly order: CustomerJourneyOrderItem; readonly showCredit: boolean }): React.JSX.Element {
  const points = order.touchpoint_sources.map((raw, index) => ({ label: touchLabel(raw), credit: order.touchpoint_credits[index] ?? 0 }));
  if (points.length === 0) return <span className="text-muted-foreground text-xs">-</span>;

  // Trailing zeros trimmed, so 100% / 33.33% / 60% read the same way for every model.
  const pct = (credit: number): string => `${String(parseFloat((credit * 100).toFixed(2)))}%`;
  const visible = points.slice(0, MAX_VISIBLE_TOUCHES);
  const remaining = points.length - MAX_VISIBLE_TOUCHES;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((point, index) => (
        <span key={index} className="flex items-center gap-1">
          <span
            title={showCredit ? `Credit: ${pct(point.credit)}` : undefined}
            className={cn("inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold", badgeClasses(point.label))}
          >
            {point.label}
            {showCredit && <span className="font-normal opacity-70">({pct(point.credit)})</span>}
          </span>
          {index < visible.length - 1 && <span className="text-muted-foreground/50 text-xs">›</span>}
        </span>
      ))}
      {remaining > 0 && (
        <HoverPanel
          trigger={
            <span className="bg-muted text-muted-foreground hover:bg-muted/70 inline-flex cursor-pointer items-center rounded border px-1.5 py-0.5 text-[10px] font-bold">
              +{remaining}
            </span>
          }
        >
          <p className="mb-1 font-semibold text-slate-200">Full Journey Path ({points.length} touchpoints):</p>
          {points.map((point, index) => (
            <p key={index} className="text-slate-300">
              {index + 1}. {point.label}
              {showCredit && ` — ${pct(point.credit)}`}
            </p>
          ))}
        </HoverPanel>
      )}
    </div>
  );
}

function TouchBadge({ source }: { readonly source: string }): React.JSX.Element {
  const label = touchLabel(source);
  return (
    <span className={cn("inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap", badgeClasses(label))}>
      {label}
    </span>
  );
}

function CampaignCell({ campaign, source, role }: { readonly campaign: string; readonly source: string; readonly role: "first" | "last" }): React.JSX.Element {
  if (campaign === "" || campaign === "-") {
    const why = role === "first" ? "Traffic source was" : "Converting touchpoint was";
    return (
      <span title={`No UTM campaign attached. ${why} ${touchLabel(source)}.`} className="text-muted-foreground cursor-default font-mono text-xs">
        -
      </span>
    );
  }
  return (
    <span title={campaign} className="block max-w-[180px] truncate font-mono text-xs">
      {campaign}
    </span>
  );
}

function ProductDetail({ value }: { readonly value: string }): React.JSX.Element {
  const isUrl = value.startsWith("http://") || value.startsWith("https://");
  if (isUrl) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
      >
        <span>View Order</span>
        <ExternalLink className="size-3 shrink-0" />
      </a>
    );
  }
  return (
    <span title={value} className="text-muted-foreground block max-w-[140px] truncate text-xs">
      {value === "" ? "-" : value}
    </span>
  );
}

function Kpi({ icon, tone, label, value }: { readonly icon: React.ReactNode; readonly tone: string; readonly label: string; readonly value: string | null }): React.JSX.Element {
  return (
    <div className="bg-card flex items-center gap-2.5 rounded-xl border px-3 py-2 shadow-xs">
      <div className={cn("flex size-7 shrink-0 items-center justify-center rounded-lg", tone)}>{icon}</div>
      <div>
        <p className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">{label}</p>
        {value === null ? <Skeleton className="mt-0.5 h-4 w-16" /> : <h3 className="text-sm font-bold tabular-nums">{value}</h3>}
      </div>
    </div>
  );
}

function Cell({ order, columnKey, showCredit }: { readonly order: CustomerJourneyOrderItem; readonly columnKey: string; readonly showCredit: boolean }): React.JSX.Element {
  switch (columnKey) {
    case "order_name":
      return (
        <span className="flex items-center gap-1 font-semibold text-blue-600">
          <span>{order.order_number}</span>
          <CopyButton value={order.order_number.replace(/^#+/, "")} label="order number" />
        </span>
      );
    case "email":
      return order.email === "" ? (
        <span className="text-muted-foreground italic">No record</span>
      ) : (
        <span className="flex items-center gap-1 font-mono text-xs">
          <span className="max-w-[160px] truncate" title={order.email}>
            {order.email}
          </span>
          <CopyButton value={order.email} label="email" />
        </span>
      );
    case "date":
      return <span className="text-muted-foreground text-xs whitespace-nowrap">{new Date(order.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>;
    case "order_value":
      return <span className="text-xs font-bold">{journeyMoney(order.order_value)}</span>;
    case "discount":
      return (
        <span className={cn("text-xs font-semibold", order.discount > 0 ? "text-rose-600" : "text-muted-foreground/60")}>
          {order.discount > 0 ? journeyMoney(order.discount) : "-"}
        </span>
      );
    case "order_status":
      return <Tag colors={orderStatusTag(order.order_status)}>{order.order_status || "Unfulfilled"}</Tag>;
    case "financial_status":
      return <Tag colors={financialTag(order.financial_status)}>{(order.financial_status || "Paid").replace(/_/g, " ")}</Tag>;
    case "user_type":
      return (
        <span
          className={cn(
            "inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-semibold",
            order.user_type === "New" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-blue-200 bg-blue-50 text-blue-700",
          )}
        >
          {order.user_type}
        </span>
      );
    case "total_ads":
      return <span className="text-xs font-semibold">{order.total_ads}</span>;
    case "unique_ads":
      return <span className="text-xs font-semibold">{order.unique_ads}</span>;
    case "conversion_time":
      return (
        <span className="flex items-center gap-1 text-xs font-medium whitespace-nowrap">
          <Clock className="text-muted-foreground size-3 shrink-0" />
          {formatConversionTime(order.conversion_time_seconds)}
        </span>
      );
    case "session_count":
      return <span className="text-xs font-semibold">{order.session_count}</span>;
    case "touch_points":
      return <TouchPoints order={order} showCredit={showCredit} />;
    case "first_touch":
      return <TouchBadge source={order.first_touch_source} />;
    case "first_touch_campaign":
      return <CampaignCell campaign={order.first_touch_campaign} source={order.first_touch_source} role="first" />;
    case "last_touch":
      return <TouchBadge source={order.last_touch_source} />;
    case "last_touch_campaign":
      return <CampaignCell campaign={order.last_touch_campaign} source={order.last_touch_source} role="last" />;
    case "location":
      return order.location === "" ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span className="text-muted-foreground flex max-w-[160px] items-center gap-1 text-xs" title={order.location}>
          <MapPin className="size-3 shrink-0" />
          <span className="truncate">{order.location}</span>
        </span>
      );
    case "product_detail":
      return <ProductDetail value={order.product_detail} />;
    case "total_items":
      return <span className="text-xs font-semibold">{order.total_items}</span>;
    case "total_quantity":
      return <span className="text-xs font-semibold">{order.total_quantity}</span>;
    case "order_source":
      return <span className="bg-muted text-muted-foreground inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold">{order.order_source || "Web"}</span>;
    default:
      return <span />;
  }
}

export function CustomerJourneyModal({
  client,
  tenantId,
  target,
  range,
  onClose,
}: {
  readonly client: AxiosInstance;
  /** A store switch refetches (the client sends X-Tenant-Id itself); same convention as every report screen. */
  readonly tenantId: string;
  readonly target: JourneyTarget;
  readonly range: DateRange;
  readonly onClose: () => void;
}): React.JSX.Element {
  const [data, setData] = useState<CustomerJourneyOrdersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const { colWidths, startResize } = useTableResize(COLUMNS.map((c) => c.width));
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [limit, setLimit] = useState<number>(25);
  const [offset, setOffset] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);
  // A response for a superseded request must never overwrite the newer one (same guard as ReportScreen).
  const ticket = useRef(0);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 400);
    return () => window.clearTimeout(handle);
  }, [search]);

  // Escape closes, focus lands on the close button, and the page behind stops scrolling.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchJourneyOrders(client, {
        from: range.from,
        to: range.to,
        search: debouncedSearch,
        sortBy: "date",
        direction: "desc",
        limit,
        offset,
        target,
      });
      if (mine === ticket.current) setData(result);
    } catch (caught) {
      if (mine !== ticket.current) return;
      setData(null);
      setError(apiErrorMessage(caught, "Could not load these orders. Please try again."));
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [client, range.from, range.to, debouncedSearch, limit, offset, target]);

  useEffect(() => {
    void load();
  }, [load, tenantId]);

  const orders = data?.orders ?? [];
  const total = data?.totalOrderCount ?? 0;
  const summary = data?.summary ?? null;
  const badge = MODEL_BADGE[target.model];
  const showCredit = target.model === "dda" || target.model === "linear" || target.model === "triple";
  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit) + 1;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Customer Journey & Orders — ${target.label}`}
        className="bg-background flex h-[90vh] w-[96vw] max-w-[1520px] flex-col gap-2.5 overflow-hidden rounded-2xl border p-4 shadow-2xl"
      >
        <div className="bg-card flex shrink-0 items-center justify-between gap-3 rounded-xl border px-4 py-2.5 shadow-xs">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-600">
              <Layers className="size-4" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-base font-bold tracking-tight">Customer Journey & Orders — {target.label}</h2>
                <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold", badge.className)}>{badge.label}</span>
                <span className="bg-muted text-muted-foreground inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold">
                  {total.toLocaleString("en-IN")} Orders
                </span>
              </div>
              <p className="text-muted-foreground text-[11px] leading-tight">
                Multi-touch journeys for {target.label} under the {badge.label} attribution model.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading} className="h-7 gap-1.5 px-2.5 text-xs">
              <RefreshCw className={cn("size-3", loading && "animate-spin")} />
              Refresh
            </Button>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer rounded-lg p-1 transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 lg:grid-cols-4">
          <Kpi icon={<ShoppingBag className="size-3.5" />} tone="bg-blue-50 text-blue-600" label="Total Orders" value={summary === null ? null : summary.totalOrders.toLocaleString("en-IN")} />
          <Kpi icon={<DollarSign className="size-3.5" />} tone="bg-emerald-50 text-emerald-600" label="Total Revenue" value={summary === null ? null : `₹${Math.round(summary.totalRevenue).toLocaleString("en-IN")}`} />
          <Kpi icon={<Clock className="size-3.5" />} tone="bg-amber-50 text-amber-600" label="Avg Conversion" value={summary === null ? null : formatConversionTime(summary.avgConversionSeconds)} />
          <Kpi icon={<TrendingUp className="size-3.5" />} tone="bg-purple-50 text-purple-600" label="Ad Touchpoints" value={summary === null ? null : summary.totalAdTouchpoints.toLocaleString("en-IN")} />
        </div>

        <div className="bg-card flex min-h-0 flex-1 flex-col gap-2 overflow-hidden rounded-xl border p-3 shadow-xs">
          <div className="flex shrink-0 items-center justify-between gap-3">
            <div className="text-muted-foreground text-xs font-medium">
              Showing <span className="text-foreground font-semibold">{orders.length}</span> of{" "}
              <span className="text-foreground font-semibold">{total.toLocaleString("en-IN")}</span> orders
            </div>
            <div className="relative w-72">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
              <Input
                type="search"
                placeholder="Search order # or product…"
                aria-label="Search orders"
                className="h-7 pl-8 text-xs"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>

          {error !== null && (
            <p className="text-destructive shrink-0 text-sm" role="alert">
              {error}
            </p>
          )}

          <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
            <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
              <colgroup>
                {colWidths.map((w, index) => (
                  <col key={COLUMNS[index]?.key ?? index} style={{ width: `${w}px` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {COLUMNS.map((column, index) => (
                    <th
                      key={column.key}
                      className={cn(
                        "row-head text-foreground sticky top-0 h-10 border-b border-r border-border px-2.5 text-[12px] font-semibold whitespace-nowrap relative",
                        column.align === "left" ? "text-left" : column.align === "right" ? "text-right" : "text-center",
                        index === 0 ? "left-0 z-30" : "z-20",
                      )}
                    >
                      <span className={cn("inline-flex items-center gap-1 pr-1", column.align === "right" && "justify-end", column.align === "center" && "justify-center")}>
                        {column.label}
                        <InfoTooltip text={column.tip} />
                      </span>
                      <TableResizeHandle onResize={(e) => startResize(index, e)} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && orders.length === 0 ? (
                  Array.from({ length: 8 }, (_, row) => (
                    <tr key={row} className={row % 2 === 1 ? "row-odd" : "row-even"}>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="border-b px-2.5 py-3">
                          <Skeleton className="h-4 w-14" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="text-muted-foreground px-4 py-14 text-center text-sm">
                      No orders found for this selection.
                    </td>
                  </tr>
                ) : (
                  orders.map((order, row) => (
                    <tr key={order.order_id} className={cn(row % 2 === 1 ? "row-odd" : "row-even", "transition-colors", loading && "opacity-60")}>
                      {COLUMNS.map((column, index) => (
                        <td
                          key={column.key}
                          className={cn(
                            "border-b px-2.5 py-2.5 align-middle",
                            column.align === "right" ? "text-right" : column.align === "center" ? "text-center" : "text-left",
                            index === 0 && `sticky left-0 z-10 border-r ${row % 2 === 1 ? "row-odd" : "row-even"}`,
                          )}
                        >
                          <Cell order={order} columnKey={column.key} showCredit={showCredit} />
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t pt-1.5">
            <div className="text-muted-foreground text-[11px] font-medium">
              Page <span className="text-foreground font-semibold">{page}</span> of <span className="text-foreground font-semibold">{pages}</span>
              {total > 0 && (
                <span className="ml-3">
                  {offset + 1}-{offset + orders.length} of {total.toLocaleString("en-IN")} orders
                </span>
              )}
            </div>
            <nav aria-label="Pagination" className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))}>
                Previous
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={offset + limit >= total || loading} onClick={() => setOffset(offset + limit)}>
                Next
              </Button>
              <Select
                aria-label="Rows per page"
                className="h-7 w-[6.5rem] text-xs"
                value={limit}
                onChange={(event) => {
                  setOffset(0);
                  setLimit(Number(event.target.value));
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </Select>
            </nav>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
